<template>
  <master-layout pageTitle="WhatsApp Compliance Keywords">
    <ComplianceKeywords 
      ref="complianceKeywords"
      channel="whatsapp"
      :initialKeywordsData="whatsappKeywords"
      :selectedTemplate="selectedTemplate"
      :previewData="previewData"
      :templatesLoading="templatesLoading"
      :finalUrlType="finalUrlType"
      :messageType="messageType"
      :textMessage="textMessage"
      :quickReplyMessage="quickReplyMessage"
      :quickReplyButtons="quickReplyButtons"
      @refresh="handleRefresh"
      @load-keywords="loadWhatsappKeywords"
      @keyword-added="handleKeywordAdded"
      @rule-selected="handleRuleSelected"
      @final-url-type="handleFinalUrlTypeUpdate"
    >
      <template #message-content>
        <div class="whatsapp-message-template">
          <selectDropDown
            :options="messageTypeOptions"
            :selectedValueProp="messageType"
            placeholder="Select Type"
            @onSelectOption="(val) => messageType = val.selectedValue"
          />
        </div>
        <div v-if="messageType === 'text'" class="whatsapp-message-template">
          <RichTextBoxV2 id="template_body" name="template_body" :value="textMessage" @input="(prop) => textMessage = prop" placeholder="Enter a message" :showSmiley="true" :showRemainingText="true" :maxLength="1024" :showCustomFiledInsertion="true" />
        </div>
        <div v-if="messageType === 'whatsappTemplate'" class="whatsapp-message-template">
          <WhatsAppCreateMessage 
            v-if="whatsappTemplates.length > 0 && selectedRule"
            ref="whatsAppMessageTemplate"
            moduleType="compliance-keywords"
            :allWhatsappTemplateData="whatsappTemplatesWithNone"
            :editBroadcast="formattedEditBroadcast"
            @alltemplateValues="handleTemplateSelection"
            @onUpdatePreview="handlePreviewUpdate"
            @final-url-type="handleUrlTypeAndForwardToParent"
            :url_type="selectedRule?.template_data?.url_type"
            :isViewMode="false"
          />
          <div v-else-if="!selectedRule" class="no-rule-selected">
            <p class="vtext-subtitle">Select a compliance keyword rule to configure template</p>
          </div>
          <div v-else-if="whatsappTemplates.length === 0" class="no-templates">
            <p class="vtext-subtitle">No approved WhatsApp templates available</p>
          </div>
        </div>
        <!-- Quick Replies: text message + postback buttons -->
        <div v-if="messageType === 'quickReplies'" class="whatsapp-message-template">
          <RichTextBoxV2
            id="qr_body"
            name="qr_body"
            :value="quickReplyMessage"
            @input="(prop) => quickReplyMessage = prop"
            placeholder="Enter a message"
            :showSmiley="true"
            :showRemainingText="true"
            :maxLength="1024"
            :showCustomFiledInsertion="true"
            :style="{ width: '450px' }"
          />
          <div class="quick-reply-button-container">
            <div v-if="quickReplyButtons.length" class="quick-reply-buttons-wrapper" :class="{ complete: quickReplyButtons.length >= 3 }">
              <div class="text-button-wrapper" v-for="(button, bIndex) in quickReplyButtons" :key="'qr-button-' + bIndex">
                <button class="btn-template-dynamic-action" :class="{ 'activeReplyButton': bIndex === qrButtonIndex }" @click="qrParseButtonInfo(bIndex)">
                  <span class="replyBttnTitle">{{ button.title }}</span>
                </button>
                <div class="text-buttons">
                  <VIcon name="close-line" :size="-5" :class="{ 'closeIconQr': bIndex === qrButtonIndex }" class="cursorPointer" @click.stop="removeQuickReplyButton(bIndex)"></VIcon>
                </div>
              </div>
            </div>
            <div class="text-button-wrapper" v-if="quickReplyButtons.length < 3">
              <button class="btn-template-dynamic-action" @click="addQuickReplyButton">
                + Add New
              </button>
            </div>
          </div>
          <div v-if="qrIsPopperOpen" class="qr-popper-container">
            <Popper :is_agent_or_group_req="true" channel="whatsapp" :showSuggestionReplies="false" :deleteButton="true" :type="qrAddEditModal" :config="qrPopperConfig" :description="true" :cancelButton="true" @submit="qrPopperSubmit" @closePopper="qrClosePopper" @deleteBttn="removeQuickReplyButton(qrButtonIndex)" :isBotbuilder="true" />
          </div>
        </div>
      </template>
    </ComplianceKeywords>
  </master-layout>
</template>

<script>
import ComplianceKeywords from '@/components/ComplianceKeywords.vue';
import WhatsAppCreateMessage from '@/pages/broadcast/whats-app-create-message.vue';
import selectDropDown from '@/components/selectDropDown.vue';
import apiInterceptor from '@/shared/apiInterceptor';
import { whatsappTemplatesQueryOptions } from '@/composables/useWhatsappTemplates';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { queryClient } from '@/shared/queryClient';
import { complianceKeywordsByChannelQueryOptions } from '@/composables/useComplianceKeywords';
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env';
import Popper from '@/components/popper.vue';
import RichTextBoxV2 from '@/components/rich-text-box-v2.vue';

export default {
  name: 'WhatsAppComplianceKeywords',
  setup() {
    const { whatsAppSocialData } = useSocialChannels();
    return { whatsAppSocialData };
  },
  components: { 
    ComplianceKeywords,
    WhatsAppCreateMessage,
    selectDropDown,
    RichTextBoxV2,
    Popper
  },
  data() {
    return {
      messageType: 'none',
      textMessage: '',
      whatsappKeywords: [],
      whatsappTemplates: [],
      templatesLoading: false,
      selectedRule: {},
      selectedTemplate: {},
      previewData: {},
      finalUrlType: 'static',
      quickReplyMessage: '',
      quickReplyButtons: [],
      qrButtonIndex: -1,
      qrAddEditModal: undefined,
      messageTypeOptions: [
        { title: 'None', value: 'none' },
        { title: 'Text', value: 'text' },
        { title: 'WhatsApp Template', value: 'whatsappTemplate' },
        { title: 'Quick Replies', value: 'quickReplies' }
      ]
    }
  },
  async mounted() {
    if (this.whatsappTemplates.length === 0) {
      await this.loadWhatsappTemplates();
    }
  },
  async ionViewWillEnter() {
    queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('whatsapp').queryKey });
    await this.loadWhatsappKeywords();
  },
  computed: {
    qrIsPopperOpen() {
      return this.qrAddEditModal === 'detailed';
    },
    qrPopperConfig() {
      if (this.qrButtonIndex > -1 && this.quickReplyButtons[this.qrButtonIndex]) {
        const button = this.quickReplyButtons[this.qrButtonIndex];
        return {
          text: button.title,
          action: button.type,
          payload: button.payload,
          description: button.description || ''
        };
      }
      return {};
    },
    whatsappTemplatesWithNone() {
      const noneOption = {
        id: 'none',
        template_id: 'none',
        template_name: 'None',
        title: 'None',
        value: 'none',
        status: 'APPROVED'
      };
      return [noneOption, ...this.whatsappTemplates];
    },
    formattedEditBroadcast() {
      // Only return data if we have a valid selected rule with non-empty template data
      if (!this.selectedRule || Object.keys(this.selectedRule).length === 0 || !this.selectedRule.template_data) {
        return {
          channel: 'whatsapp',
          template_data: null // Include null template_data for auto-selection
        };
      }
      
      // Check if template data is empty
      if (this.isTemplateEmpty(this.selectedRule.template_data)) {
        return {
          channel: 'whatsapp',
          template_data: this.selectedRule.template_data // Include the empty template_data for auto-selection
        };
      }
      
      const templateData = this.selectedRule.template_data;
      return {
        // Template ID fields - WhatsApp component checks for both
        wa_templateid: templateData.wa_templateid || templateData.template_id,
        wa_templategid: templateData.wa_templateid || templateData.template_id,
        // Message parameters for media, buttons, etc.
        wa_message_params: templateData.message_params || {},
        // Custom field values
        custom_messages: templateData.custom_messages || {},
        // Include rule ID for edit mode detection
        id: this.selectedRule.id,        
        channel: 'whatsapp'
      };
    }
  },
  methods: {
    // --- Quick Replies Popper integration ---
    qrParseButtonInfo(index) {
      if (this.qrIsPopperOpen) {
        useUIStore().updateToast({ open: true, message: 'Please save your changes to the current button before editing a new one.', type: 'warning' });
        return;
      }
      this.qrAddEditModal = 'detailed';
      this.qrButtonIndex = index;
    },
    qrClosePopper() {
      this.qrAddEditModal = undefined;
      this.qrButtonIndex = -1;
    },
    qrPopperSubmit(result) {
      const data = result.new;
      // Normalize compound actions to postback
      switch (data.action) {
        case 'bot_flow':
        case 'agent_or_group':
        case 'opt_in':
        case 'opt_out':
        case 'livechat':
        case 'custom':
          data.action = 'postback';
          break;
      }
      if (this.qrButtonIndex > -1 && this.quickReplyButtons[this.qrButtonIndex]) {
        const button = this.quickReplyButtons[this.qrButtonIndex];
        button.title = data.text;
        button.payload = data.payload;
        button.description = data.description || '';
        button.type = data.action || 'postback';
      }
      this.qrAddEditModal = undefined;
      this.qrButtonIndex = -1;
    },
    addQuickReplyButton() {
      if (this.quickReplyButtons.length < 3) {
        this.quickReplyButtons.push({ title: 'Button', payload: undefined, description: '', type: 'postback' });
        this.$nextTick(() => {
          this.qrParseButtonInfo(this.quickReplyButtons.length - 1);
        });
      }
    },
    removeQuickReplyButton(bIndex) {
      if (bIndex >= 0 && bIndex < this.quickReplyButtons.length) {
        this.quickReplyButtons.splice(bIndex, 1);
      }
      this.qrAddEditModal = undefined;
      this.qrButtonIndex = -1;
    },
    // ----------------------------------------------------------
    async handleRefresh() {
      queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('whatsapp').queryKey });
      await this.loadWhatsappKeywords();
      // Only reload templates if we don't have any loaded
      if (this.whatsappTemplates.length === 0) {
        await this.loadWhatsappTemplates();
      }
    },
    async loadWhatsappKeywords() {
      try {
        // fetchQuery (not ensureQueryData) is required here: after invalidateQueries marks the
        // entry stale, fetchQuery will re-fetch; ensureQueryData returns stale cache as-is.
        const whatsappCompliance = await queryClient.fetchQuery(complianceKeywordsByChannelQueryOptions('whatsapp'));
        // Transform API response to component format
        this.whatsappKeywords = (Array.isArray(whatsappCompliance) ? whatsappCompliance : []).map(item => ({
          id: item.id,
          keywords: item.keywords || [],
          type: item.type === 'opt_in' ? 'Opt-in' : 'Opt-out',
          description: item.description,
          active: item.active,
          channel: item.channel,
          seller_id: item.seller_id,
          message: item.message,
          url_types: item.url_types,
          template_data: item.template_data
        }));
        // Update the ComplianceKeywords component with the loaded data
        this.$refs.complianceKeywords?.updateKeywordsData(this.whatsappKeywords);
      } catch (error) {
        this.whatsappKeywords = [];
        this.$refs.complianceKeywords?.updateKeywordsData([]);
        useUIStore().updateToast({ open: true, message: error.message || "Failed to load WhatsApp compliance keywords", type: "danger" });
      }
    },
    async handleKeywordAdded(data) {
      try {
        this.$refs.complianceKeywords?.setLoading(true);
        
        // Build message payload based on messageType
        let messagePayload = {};
        let templateData = this.selectedRule?.template_data || {};

        if (this.messageType === 'text') {
          // Text message payload
          if (!this.textMessage.trim()) {
            useUIStore().updateToast({ 
              open: true, 
              message: "Please enter a text message", 
              type: "danger" 
            });
            this.$refs.complianceKeywords?.setLoading(false);
            return;
          }
          messagePayload = {
            message: [this.textMessage],
            template_type: "normalchat"
          };
          // Clear any previously saved template association
          templateData = {};
        } else if (this.messageType === 'quickReplies') {
          if (!this.quickReplyMessage || !this.quickReplyMessage.trim()) {
            useUIStore().updateToast({ open: true, message: 'Please enter a text message for quick replies', type: 'danger' });
            this.$refs.complianceKeywords?.setLoading(false);
            return;
          }
          if (this.quickReplyButtons.length === 0) {
            useUIStore().updateToast({ open: true, message: 'Please add at least one quick reply button', type: 'danger' });
            this.$refs.complianceKeywords?.setLoading(false);
            return;
          }
          const invalidButton = this.quickReplyButtons.find(b => !b.title || !b.payload);
          if (invalidButton) {
            useUIStore().updateToast({ open: true, message: 'Please configure all quick reply buttons (title and action are required)', type: 'danger' });
            this.$refs.complianceKeywords?.setLoading(false);
            return;
          }
          messagePayload = {
            message: [this.quickReplyMessage],
            template_type: 'quickreplies',
            template: {
              buttons: this.quickReplyButtons.map(b => ({
                title: b.title,
                description: b.description || '',
                type: b.type || 'postback',
                payload: b.payload,
              }))
            }
          };
          templateData = {};
        } else if (this.messageType === 'whatsappTemplate') {
          // Template selection is now optional - validate only if template is selected
          if (this.selectedTemplate?.id && this.$refs.whatsAppMessageTemplate) {
            if (!this.validateTemplateSelection()) {
              this.$refs.complianceKeywords?.setLoading(false);
              return;
            }
            // Get template data from WhatsApp message component
            templateData = this.getSelectedRuleTemplateData();
          }
        }

        let reqBody = {
          keywords: data.keywords,
          type: data.type, // opt_in or opt_out
          description: data.description || "",
          active: data.active,
          channel: data.channel,
          url_types: data.url_types || {},
        };

        // Only include message and template_data when a message type is configured
        if (this.messageType !== 'none') {
          reqBody.message = messagePayload;
          reqBody.template_data = templateData;
        }
        // messageType === 'none' -> don't pass message and template_data

        // Check if we're editing existing keywords (has ID) or creating new ones
        if (data.id) {
          // updating existing compliance keywords
          await apiInterceptor.put(environment("VUE_APP_API_URL") + `social/v1/compliance/${data.id}`, reqBody);
          useUIStore().updateToast({ open: true, message: "Compliance keywords updated successfully", type: "success" });
        } else {
          //  creating new compliance keywords
          await apiInterceptor.post(environment("VUE_APP_API_URL") + "social/v1/compliance", reqBody);
          useUIStore().updateToast({ open: true, message: "Compliance keywords created successfully", type: "success" });
        }
        
        // Invalidate only the WhatsApp-scoped cache key so SMS data is unaffected,
        // then reload — fetchQuery will re-fetch now that the entry is stale.
        queryClient.invalidateQueries({ queryKey: complianceKeywordsByChannelQueryOptions('whatsapp').queryKey });
        await this.loadWhatsappKeywords();
        
        // Navigate back to keywords table first to hide the WhatsApp component
        this.$refs.complianceKeywords?.resetToKeywordsTable();
        
        // Clear selected data after navigation to prevent unwanted API calls
        this.$nextTick(() => {
          this.selectedRule = {};
          this.selectedTemplate = {};
          this.previewData = {};
          this.messageType = 'none';
          this.textMessage = '';
          this.quickReplyMessage = '';
          this.quickReplyButtons = [];
          this.qrButtonIndex = -1;
          this.qrAddEditModal = undefined;
        });
      } catch (error) {
        useUIStore().updateToast({ open: true, message: error.message || "Failed to save compliance keywords", type: "danger" });
      } finally {
        this.$refs.complianceKeywords?.setLoading(false);
      }
    },
    async loadWhatsappTemplates() {
      if (!this.whatsAppSocialData?.id) {
        this.whatsappTemplates = [];
        return;
      }
      try {
        this.templatesLoading = true;
        const response = await queryClient.fetchQuery(whatsappTemplatesQueryOptions(this.whatsAppSocialData.id, { size: '-1' }));
        const templates = (response.templates || []).filter(
          (objTemplate) => objTemplate && objTemplate?.status === 'APPROVED'
        );
        this.whatsappTemplates = templates;
        this.whatsappTemplates.sort((a, b) => a.template_name.localeCompare(b.template_name));
      } catch (error) {
        console.error('Failed to load WhatsApp templates:', error);
        useUIStore().updateToast({ open: true, message: error.message || "Failed to load WhatsApp templates", type: "danger" });
        this.whatsappTemplates = [];
      } finally {
        this.templatesLoading = false;
      }
    },
    handleTemplateSelection(templateData) {
      if (templateData.id === 'none' || templateData.template_id === 'none') {
        this.clearTemplate();
        return;
      }
      
      this.selectedTemplate = templateData;
      if (this.$refs.complianceKeywords?.selectedRule) {
        const currentTemplateData = this.$refs.complianceKeywords.selectedRule.template_data || {};
        this.$refs.complianceKeywords.selectedRule.template_data = {
          ...currentTemplateData,
          template_id: templateData.id,
          wa_templateid: templateData.id,
          message_params: {
            ...currentTemplateData.message_params,
            HEADER: []
          }
        };
      }
    },
    clearTemplate() {
      try {
        if (this.selectedRule && this.selectedRule.template_data) {
          this.selectedRule.template_data = {
            template_id: null,
            wa_templateid: null,
            message_params: {},
            custom_messages: {},
            url_type: null
          };
        }
        this.selectedTemplate = {};
        this.previewData = {};
        
        // Reset WhatsApp message component
        if (this.$refs.whatsAppMessageTemplate) {
          this.$refs.whatsAppMessageTemplate.resetTemplateValues();
        }        
      } catch (error) {
        console.error('Error clearing template:', error);
      }
    },
    handlePreviewUpdate(previewData) {
      this.previewData = previewData;
    },
    handleUrlTypeUpdate(urlType) {
      if (this.selectedRule && this.selectedRule.template_data) {
        this.selectedRule.template_data.url_type = urlType;
      }
    },
    handleFinalUrlTypeUpdate(urlType) {
      this.finalUrlType = urlType;
      // Also forward to handleUrlTypeUpdate for consistency
      this.handleUrlTypeUpdate(urlType);
    },
    handleUrlTypeAndForwardToParent(urlType) {
      // Update local state
      this.handleUrlTypeUpdate(urlType);
      // Forward to ComplianceKeywords component
      if (this.$refs.complianceKeywords) {
        this.$refs.complianceKeywords.handleUrlTypeUpdate(urlType);
      }
    },
    async handleRuleSelected(rule) {
      this.selectedRule = rule;
      this.selectedTemplate = {};
      this.previewData = {};
      
      // Detect messageType from saved rule data
      // API returns message.type = 'normalchat' with message.message as a string
      // Local save format uses message.template_type = 'normalchat' with message.message as an array
      // Priority: template_data > message, because backend may not clear old message when template is saved
      const msgType = rule?.message?.type || rule?.message?.template_type;
      const msgContent = rule?.message?.message;
      // Reset quick reply state first
      this.quickReplyMessage = '';
      this.quickReplyButtons = [];
      this.qrAddEditModal = undefined;
      this.qrButtonIndex = -1;

      if (!this.isTemplateEmpty(rule?.template_data)) {
        // WhatsApp template type - check first as template_data takes priority
        this.messageType = 'whatsappTemplate';
        this.textMessage = '';
      } else if (msgType?.startsWith('quickreplies') && msgContent) {
        // Quick replies type - check message type and content existence")
        this.messageType = 'quickReplies';
        this.quickReplyMessage = Array.isArray(msgContent) ? msgContent[0] : msgContent;
        this.quickReplyButtons = (rule?.message?.template?.buttons || rule?.message?.buttons || []).map(b => ({
          title: b.title || 'Button',
          description: b.extrainfo?.description || b.description || '',
          type: b.type || 'postback',
          payload: b.payload || '',
        }));
        this.textMessage = '';
      } else if (msgType === 'normalchat' && msgContent) {
        this.messageType = 'text';
        this.textMessage = Array.isArray(msgContent) ? msgContent[0] : msgContent;
      } else {
        this.messageType = 'none';
        this.textMessage = '';
      }
      
      if (rule?.template_data?.url_type) {
        this.finalUrlType = rule.template_data.url_type;
      } else {
        this.finalUrlType = 'static';
      }
      
      this.$nextTick(async () => {
        if (this.$refs.whatsAppMessageTemplate) {
          // Check if rule has empty template data and set "none" in dropdown
          const hasEmptyTemplate = this.isTemplateEmpty(rule.template_data);
          
          if (hasEmptyTemplate) {
            console.log('Rule has empty template, setting dropdown to "none"');
            // Set the dropdown to "none" for empty templates
            await this.setWhatsAppTemplateToNone();
            return;
          }
          
          if (rule.template_data && (rule.template_data.wa_templateid || rule.template_data.template_id)) {
            const templateId = rule.template_data.wa_templateid || rule.template_data.template_id;
            
            const foundTemplate = this.whatsappTemplates.find(t => 
              t.id === templateId || t.template_id === templateId || t.template_name === templateId
            );
                        
            if (foundTemplate) {
              this.selectedTemplate = foundTemplate;
              
              useUIStore().updateLoading({ isLoading: true, message: 'Loading template data...' });
              
              try {
                await this.$refs.whatsAppMessageTemplate.preLoadValues();
              } catch (error) {
                console.error('Error preloading template data:', error);
              } finally {
                useUIStore().updateLoading({ isLoading: false });
              }
            }
          } else {
            console.log('No existing template data');
            // Always reset if no template data
            this.$refs.whatsAppMessageTemplate.resetTemplateValues();
          }
        }
      });
    },
    getSelectedRuleTemplateData() {
      // Get template data from the WhatsApp message component
      if (this.$refs.whatsAppMessageTemplate) {
        try {
          const templateData = this.$refs.whatsAppMessageTemplate.getBroadcastMessageParamsToSave();
          return {
            template_id: templateData.waTemplateId,
            message_params: templateData.message_params,
            custom_messages: templateData.custom_messages,
            url_type: this.selectedRule?.template_data?.url_type
          };
        } catch (error) {
          console.error('Error getting template data:', error);
          return {};
        }
      }
      return {};
    },
    isTemplateEmpty(templateData) {
      // Check various conditions for empty templates
      if (!templateData) return true;
      if (typeof templateData === 'object' && Object.keys(templateData).length === 0) return true;
      if (!templateData.template_id && !templateData.wa_templateid) return true;
      if (templateData.template_id === null || templateData.wa_templateid === null) return true;
      if (templateData.template_id === 'none' || templateData.wa_templateid === 'none') return true;
      return false;
    },
    async setWhatsAppTemplateToNone() {
      try {
        // Set the selected template to "none"
        this.selectedTemplate = {
          id: 'none',
          template_id: 'none',
          template_name: 'None - No Template'
        };
        
        // Update the WhatsApp component's template selection
        if (this.$refs.whatsAppMessageTemplate) {
          this.$refs.whatsAppMessageTemplate.waTemplateId = 'none';
          this.$refs.whatsAppMessageTemplate.resetTemplateValues();
        }        
      } catch (error) {
        console.error('Error setting WhatsApp template to none:', error);
      }
    },
    validateTemplateSelection() {
      if (!this.selectedRule) {
        useUIStore().updateToast({ open: true, message: "Please select a compliance rule first", type: "danger" });
        return false;
      }

      // If no template is selected, it's still valid (optional template)
      if (!this.selectedTemplate?.id || !this.$refs.whatsAppMessageTemplate) {
        return true;
      }
      try {
        const isValid = this.$refs.whatsAppMessageTemplate.isValidToSubmit();
        if (!isValid) {
          useUIStore().updateToast({ open: true, message: "Please complete the template configuration", type: "danger" });
          return false;
        }
        return true;
      } catch (error) {
        console.error('Error validating template:', error);
        useUIStore().updateToast({ open: true, message: "Error validating template configuration", type: "danger" });
        return false;
      }
    }
  }
}
</script>

<style scoped>
.whatsapp-message-template {
  padding: 16px 0;
}

/* ---- Quick Replies Builder ---- */
.quick-reply-button-container {
  display: flex;
  flex-wrap: nowrap;
  column-gap: 10px;
  max-width: 100%;
  overflow-x: auto;
  padding: 10px 0;
}
.quick-reply-button-container::-webkit-scrollbar {
  height: 5px;
  background-color: #f5f5f5;
}
.quick-reply-button-container::-webkit-scrollbar-thumb {
  background-color: var(--purple);
  border-radius: 5px;
}
.quick-reply-buttons-wrapper {
  display: flex;
  column-gap: 10px;
}
.text-button-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  flex-shrink: 0;
}
.btn-template-dynamic-action {
  cursor: pointer;
  min-width: 90px;
  height: 32px;
  border: 1px solid var(--purple);
  color: var(--purple);
  background: var(--white);
  border-radius: 20px;
  font: inherit;
  font-size: 13px;
  padding: 0 14px;
  transition: all 0.2s;
}
.btn-template-dynamic-action:hover {
  background: var(--purple-opac);
}
.text-buttons {
  position: absolute;
  display: flex;
  height: 100%;
  align-items: center;
  right: 3px;
}
.replyBttnTitle {
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}
.activeReplyButton {
  background: var(--purple);
  border-color: var(--purple);
  color: var(--white);
}
.activeReplyButton .replyBttnTitle {
  color: var(--white);
}
.closeIconQr {
  color: var(--white) !important;
}
.qr-popper-container {
  margin-top: 16px;
  border-top: 1px solid var(--background-primary);
  padding-top: 12px;
}

.no-templates,
.no-rule-selected {
  padding: 20px;
  text-align: center;
  background: var(--background-primary);
  border-radius: 8px;
  margin: 8px 0;
}
</style>
